import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { workspaceSummary } from '../../lib/workspace-summary'
import { RiAddLine, RiBuilding2Line, RiSearchLine } from '@remixicon/react'
import type { WorkspaceListItem } from 'shared'
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  SectionSpinner,
} from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate, formatRelativeTime } from '../../lib/dates'
import { IDENTITY_BADGE, monogram } from '../../lib/identity'
import { cx, focusRing } from '../../lib/utils'

export default function WorkspacesList() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  useEffect(() => {
    api
      .get<{ data: WorkspaceListItem[] }>('/api/workspaces')
      .then((response) => setWorkspaces(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [])

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase()
    return workspaces.filter((workspace) => {
      if (!showArchived && workspace.status === 'archived') return false
      if (!term) return true
      return (
        workspace.name.toLowerCase().includes(term) ||
        (workspace.industry ?? '').toLowerCase().includes(term)
      )
    })
  }, [workspaces, query, showArchived])

  return (
    <>
      <PageHeader
        title={t('workspaces.title')}
        subtitle={t('workspaces.subtitle')}
        actions={
          <Button variant="cta" onClick={() => navigate('/app/workspaces/new')}>
            <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
            {t('workspaces.new')}
          </Button>
        }
      />

      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>
      ) : workspaces.length === 0 ? (
        <EmptyState
          icon={<RiBuilding2Line className="size-6" aria-hidden="true" />}
          title={t('workspaces.emptyTitle')}
          description={t('workspaces.emptyDescription')}
          action={
            <Button onClick={() => navigate('/app/workspaces/new')}>{t('workspaces.new')}</Button>
          }
        />
      ) : (
        <>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <RiSearchLine
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('workspaces.searchPlaceholder')}
                aria-label={t('common.search')}
                className="pl-9"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
                className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
              />
              {t('workspaces.showArchived')}
            </label>
          </div>

          {visible.length === 0 ? (
            <EmptyState title={t('common.noResults')} />
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {visible.map((workspace) => {
                return (
                  /* A grid item defaults to `min-width: auto`, so it refuses
                     to shrink below its content and the card's own padding
                     then pushes the whole row past the screen on a phone. */
                  <li key={workspace.id} className="min-w-0">
                    <Link
                      to={`/app/workspaces/${workspace.id}`}
                      className={cx(
                        'group relative flex h-full flex-col overflow-hidden rounded-xl bg-white p-5 ring-1 ring-gray-200 transition duration-150',
                        'hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-900/5 hover:ring-gray-300',
                        'dark:bg-gray-950 dark:ring-gray-800 dark:hover:ring-gray-700',
                        focusRing,
                      )}
                    >

                      <div className="flex items-start gap-3">
                        <span
                          aria-hidden="true"
                          className={cx(
                            'flex size-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold ring-1',
                            IDENTITY_BADGE,
                          )}
                        >
                          {monogram(workspace.name)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h2 className="truncate text-display-sm text-gray-950 dark:text-white">
                            {workspace.name}
                          </h2>
                          <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                            {workspaceSummary(workspace, t) || '—'}
                          </p>
                        </div>
                        {workspace.status === 'archived' && (
                          // shrink-0: the badge never wraps, so without it a
                          // long company name squeezes it into a clipped stub.
                          <Badge variant="neutral" className="shrink-0">
                            {t('workspaces.archived')}
                          </Badge>
                        )}
                      </div>

                      {/* Last activity, not creation date: an operator scanning
                          a wall of companies is asking what moved. It comes
                          from the timeline, so it means what it says; a
                          company where nothing has happened yet says that
                          rather than borrowing another date to look busy. */}
                      <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
                        <span>{t('workspaces.memberCount', { count: workspace.member_count })}</span>
                        {workspace.last_activity_at ? (
                          <span title={formatDate(workspace.last_activity_at, i18n.language)}>
                            {t('workspaces.lastActivity', {
                              when: formatRelativeTime(workspace.last_activity_at, i18n.language),
                            })}
                          </span>
                        ) : (
                          <span>{t('workspaces.noActivity')}</span>
                        )}
                      </div>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </>
  )
}
